import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import './Invoices.css';

const InvoiceModal = ({ show, onHide, projectId, onInvoiceCreated }) => {
  const { token } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [outstandingPayments, setOutstandingPayments] = useState([]);
  const [totalDue, setTotalDue] = useState(0);
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (show && projectId) {
      fetchPaymentData();
    }
  }, [show, projectId]);

  const fetchPaymentData = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/payment-tracking/project/${projectId}`, {
        headers: { 'x-auth-token': token }
      });

      if (!response.ok) throw new Error('Failed to fetch payment data');

      const data = await response.json();
      
      // Filter for outstanding payments only
      const outstanding = data.payments.filter(p => p.Status !== 'received');
      setOutstandingPayments(outstanding);
      
      // Calculate total
      const total = outstanding.reduce((sum, p) => sum + parseFloat(p.Amount || 0), 0);
      setTotalDue(total);
    } catch (err) {
      console.error('Error fetching payment data:', err);
      toast.error('Failed to load payment data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (outstandingPayments.length === 0) {
      toast.warning('No outstanding payments to invoice');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch('/api/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          projectId,
          notes,
          dueDate: dueDate || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create invoice');
      }

      const data = await response.json();
      
      // Reset form
      setNotes('');
      setDueDate('');
      
      // Close modal and trigger callback to refresh and switch to documents tab
      // The parent component will show the email preview modal
      onHide();
      if (onInvoiceCreated) {
        onInvoiceCreated(data);
      }
      
    } catch (err) {
      console.error('Error generating invoice:', err);
      toast.error(err.message || 'Failed to generate invoice');
    } finally {
      setIsGenerating(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Generate Invoice</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Loading payment data...</p>
          </div>
        ) : (
          <>
            {/* Outstanding Payments Section */}
            <div className="mb-4">
              <h5>Outstanding Payments</h5>
              {outstandingPayments.length === 0 ? (
                <Alert variant="success">
                  <i className="bi bi-check-circle me-2"></i>
                  All payments have been received! No outstanding balance.
                </Alert>
              ) : (
                <div className="outstanding-payments-list">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Due Date</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outstandingPayments.map((payment, index) => (
                        <tr key={index}>
                          <td>
                            {payment.Description}
                            {payment.IsChangeOrder && (
                              <Badge bg="info" className="ms-2">Change Order</Badge>
                            )}
                          </td>
                          <td>{payment.DueDate ? formatDate(payment.DueDate) : 'Upon receipt'}</td>
                          <td className="text-end">{formatCurrency(payment.Amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-dark">
                        <td colSpan="2"><strong>Total Due</strong></td>
                        <td className="text-end"><strong>{formatCurrency(totalDue)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Invoice Options */}
            {outstandingPayments.length > 0 && (
              <div className="invoice-options mb-4">
                <h5>Invoice Options</h5>
                <Form.Group className="mb-3">
                  <Form.Label>Due Date (Optional)</Form.Label>
                  <Form.Control
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Leave blank for "Upon Receipt"
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Notes (Optional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any notes to appear on the invoice..."
                  />
                </Form.Group>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        {outstandingPayments.length > 0 && (
          <Button 
            variant="primary" 
            onClick={handleGenerateInvoice}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Generating...
              </>
            ) : (
              <>
                <i className="bi bi-file-earmark-text me-2"></i>
                Generate Invoice
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default InvoiceModal;
